/**
 * Macchina a stati per il workflow delle lavorazioni.
 */

import { ROLES } from '@/lib/roles';
import type { Role } from '@/lib/roles';
import { getDocumentController } from '@/lib/practicePermissions';
import type { PracticeWithController } from '@/lib/practicePermissions';

export const WORKFLOW_STATES = {
  NON_AVVIATO: 'non_avviato',
  IN_CORSO: 'in_corso',
  APPROVAZIONE_HQ: 'approvazione_hq',
  CHIUSO: 'chiuso',
  RIFIUTATO: 'rifiutato',
  RIFIUTATO_DITTA: 'rifiutato_ditta',
} as const;

export type WorkflowState = typeof WORKFLOW_STATES[keyof typeof WORKFLOW_STATES];

export const STATE_LABELS: Record<WorkflowState, string> = {
  [WORKFLOW_STATES.NON_AVVIATO]: 'Non avviato',
  [WORKFLOW_STATES.IN_CORSO]: 'In corso',
  [WORKFLOW_STATES.APPROVAZIONE_HQ]: 'Documenti approvati',
  [WORKFLOW_STATES.CHIUSO]: 'Chiuso',
  [WORKFLOW_STATES.RIFIUTATO]: 'Rifiutato',
  [WORKFLOW_STATES.RIFIUTATO_DITTA]: 'Rifiutato dalla ditta',
};

export const STATE_COLORS: Record<WorkflowState, { bg: string; text: string; border: string }> = {
  [WORKFLOW_STATES.NON_AVVIATO]: { bg: 'bg-fw-light', text: 'text-fw-muted', border: 'border-fw-border' },
  [WORKFLOW_STATES.IN_CORSO]: { bg: 'bg-yellow-50', text: 'text-fw-dark', border: 'border-fw-yellow/30' },
  [WORKFLOW_STATES.APPROVAZIONE_HQ]: { bg: 'bg-blue-50', text: 'text-fw-blue', border: 'border-fw-blue/30' },
  [WORKFLOW_STATES.CHIUSO]: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-fw-green/30' },
  [WORKFLOW_STATES.RIFIUTATO]: { bg: 'bg-red-50', text: 'text-fw-red', border: 'border-fw-red/30' },
  [WORKFLOW_STATES.RIFIUTATO_DITTA]: { bg: 'bg-red-50', text: 'text-fw-red', border: 'border-fw-red/30' },
};

/**
 * Stato "business" aggregato: la pratica e' viva finche' non e' chiusa.
 * Tutti gli stati transitori collassano a 'in_corso'. Il badge secondario
 * (vedi SUB_STATE_*) specifica la fase precisa.
 */
export const PRIMARY_STATES = {
  NON_AVVIATO: 'non_avviato',
  IN_CORSO: 'in_corso',
  CHIUSO: 'chiuso',
} as const;

export type PrimaryState = typeof PRIMARY_STATES[keyof typeof PRIMARY_STATES];

export const PRIMARY_STATE_LABELS: Record<PrimaryState, string> = {
  [PRIMARY_STATES.NON_AVVIATO]: 'Non avviato',
  [PRIMARY_STATES.IN_CORSO]: 'In corso',
  [PRIMARY_STATES.CHIUSO]: 'Chiuso',
};

export const PRIMARY_STATE_COLORS: Record<PrimaryState, { bg: string; text: string }> = {
  [PRIMARY_STATES.NON_AVVIATO]: { bg: 'bg-fw-light', text: 'text-fw-muted' },
  [PRIMARY_STATES.IN_CORSO]: { bg: 'bg-yellow-50', text: 'text-fw-dark' },
  [PRIMARY_STATES.CHIUSO]: { bg: 'bg-green-50', text: 'text-green-700' },
};

export function getPrimaryStatus(ws: WorkflowState | string | null | undefined): PrimaryState {
  if (ws === WORKFLOW_STATES.NON_AVVIATO) return PRIMARY_STATES.NON_AVVIATO;
  if (ws === WORKFLOW_STATES.CHIUSO) return PRIMARY_STATES.CHIUSO;
  if (!ws) return PRIMARY_STATES.NON_AVVIATO;
  return PRIMARY_STATES.IN_CORSO;
}

/**
 * Label del sotto-stato (fase specifica del workflow). Per 'in_corso' e
 * 'rifiutato' il controllore documenti (HQ o Fastweb) viene interpolato
 * dalla pratica. Ritorna null per stati senza sotto-stato utile
 * (non_avviato, chiuso).
 */
export function getSubStatusLabel(
  ws: WorkflowState | string | null | undefined,
  practice?: PracticeWithController | null,
): string | null {
  const controller = practice ? getDocumentController(practice) : 'hq';
  const controllerName = controller === 'fastweb' ? 'Fastweb' : 'HQ';
  switch (ws) {
    case WORKFLOW_STATES.IN_CORSO: return `In verifica ${controllerName}`;
    case WORKFLOW_STATES.APPROVAZIONE_HQ: return 'In attesa Ditta';
    case WORKFLOW_STATES.RIFIUTATO: return `Documenti rifiutati da ${controllerName}`;
    case WORKFLOW_STATES.RIFIUTATO_DITTA: return 'Preventivo rifiutato da Ditta';
    default: return null;
  }
}

export const SUB_STATE_COLORS: Record<string, { bg: string; text: string }> = {
  [WORKFLOW_STATES.IN_CORSO]: { bg: 'bg-yellow-50', text: 'text-fw-dark' },
  [WORKFLOW_STATES.APPROVAZIONE_HQ]: { bg: 'bg-blue-50', text: 'text-fw-blue' },
  [WORKFLOW_STATES.RIFIUTATO]: { bg: 'bg-red-50', text: 'text-fw-red' },
  [WORKFLOW_STATES.RIFIUTATO_DITTA]: { bg: 'bg-red-50', text: 'text-fw-red' },
};

/**
 * Stati "attivi" = la pratica e' in lavorazione ma non ancora chiusa.
 * Usato dalla Dashboard card "In corso" per contare tutte le pratiche
 * vive (escluse non_avviato e chiuso). Simmetrico a getPrimaryStatus
 * === 'in_corso'.
 */
export const ACTIVE_STATES: WorkflowState[] = [
  WORKFLOW_STATES.IN_CORSO,
  WORKFLOW_STATES.APPROVAZIONE_HQ,
  WORKFLOW_STATES.RIFIUTATO,
  WORKFLOW_STATES.RIFIUTATO_DITTA,
];

/**
 * Ruolo di una transizione:
 *  - Role concreto (fornitore, hq, fastweb, ditta) -> solo quel ruolo puo' eseguirla
 *  - 'system'              -> automatica, non attivabile dall'UI
 *  - 'document_controller' -> HQ o Fastweb, dipende da practice.fornitoreIsInternalHq
 *                             (risolto via practicePermissions.getDocumentController)
 */
type TransitionRole = Role | 'system' | 'document_controller';

interface Transition {
  to: WorkflowState;
  role: TransitionRole;
  label: string;
}

const TRANSITIONS: Record<WorkflowState, Transition[]> = {
  [WORKFLOW_STATES.NON_AVVIATO]: [
    { to: WORKFLOW_STATES.IN_CORSO, role: 'system', label: 'Doc caricati (automatico)' },
  ],
  [WORKFLOW_STATES.IN_CORSO]: [
    { to: WORKFLOW_STATES.APPROVAZIONE_HQ, role: 'document_controller', label: 'Approva doc' },
    { to: WORKFLOW_STATES.RIFIUTATO, role: 'document_controller', label: 'Rifiuta' },
  ],
  [WORKFLOW_STATES.APPROVAZIONE_HQ]: [
    { to: WORKFLOW_STATES.CHIUSO, role: ROLES.DITTA, label: 'Approva preventivo' },
    { to: WORKFLOW_STATES.RIFIUTATO_DITTA, role: ROLES.DITTA, label: 'Rifiuta preventivo' },
  ],
  [WORKFLOW_STATES.RIFIUTATO]: [
    { to: WORKFLOW_STATES.NON_AVVIATO, role: ROLES.FORNITORE, label: 'Riapri' },
  ],
  [WORKFLOW_STATES.RIFIUTATO_DITTA]: [
    { to: WORKFLOW_STATES.APPROVAZIONE_HQ, role: ROLES.FORNITORE, label: 'Aggiorna preventivo' },
  ],
  [WORKFLOW_STATES.CHIUSO]: [],
};

/**
 * Risolve il ruolo effettivo di una transizione data la pratica.
 * Per 'document_controller' consulta practicePermissions.getDocumentController.
 * Se la pratica non e' nota, fa fallback a HQ (comportamento legacy = pratiche normali).
 */
function resolveTransitionRole(
  transition: Transition,
  practice?: PracticeWithController | null
): TransitionRole {
  if (transition.role === 'document_controller') {
    return getDocumentController(practice);
  }
  return transition.role;
}

/**
 * Ritorna le transizioni disponibili per lo stato/ruolo corrente (esclude 'system').
 * Il parametro `practice` e' opzionale per compatibilita': se assente, le transizioni
 * di tipo 'document_controller' vengono risolte con il fallback a HQ.
 */
export function getAvailableTransitions(
  currentState: string,
  userRole: string,
  practice?: PracticeWithController | null
): Transition[] {
  const transitions = TRANSITIONS[currentState as WorkflowState] || [];
  return transitions.filter((t) => resolveTransitionRole(t, practice) === userRole);
}

/**
 * Verifica se una transizione specifica e' permessa.
 * Le transizioni 'system' richiedono esplicitamente role='system'.
 */
export function canTransition(
  currentState: string,
  targetState: string,
  userRole: string,
  practice?: PracticeWithController | null
): boolean {
  const transitions = TRANSITIONS[currentState as WorkflowState] || [];
  return transitions.some(
    (t) => t.to === targetState && resolveTransitionRole(t, practice) === userRole
  );
}
